import type { SheetMirrorTopRowDto } from '@bhops/shared';

// 시트 탭 머리글 위쪽 요약 칸(2026-10-23) — 오너: 도입문의 탭 "그 위에 있는 것들도 콘솔에 맞게 구현해야돼".
// 스크립트가 보낸 칸(보이는 글자 · 바탕색 그대로)을 시트 배치에서 읽어 콘솔 카드로 나눈다. 값은 고치지 않는다
// (시트의 수식 · 손으로 넣은 목표가 정본). 달마다 배치가 조금씩 달라(26.03 '목표량' · 26.04 '목표건') 이름이
// 아니라 모양으로 찾는다:
//   · 표(성과)   — 오른쪽 · 아래가 모두 이름인 모서리 칸('성과'). 열 이름 × 줄 이름(목표건 · 달성건 · 달성률)
//   · 숫자 묶음 — 이름 줄 바로 아래 줄에 같은 열로 값('1주차' → 19). 빈 열로 묶음을 나눈다.
//   · 비율 칩   — 'Dylan: 63건 (47.4%)' 처럼 '이름: 값' 한 칸
//   · 나머지     — 위 셋에 안 든 칸(rest). 화면에는 보이지 않는다(2026-10-23 오너 "요약에서 빼" — 26.08 AF2:AI3 메모 계산)

export interface TopCell {
  text: string;
  bg?: string;
}
export interface TopGoal {
  /** 열 이름('인바운드') */
  label: string;
  target?: string;
  done?: string;
  /** '100%' */
  rate?: string;
  /** 그 밖의 줄(줄 이름, 값) */
  extra: [string, string][];
}
export interface TopStatGroup {
  /** 묶음 왼쪽의 이름 칸('IB 현황') */
  title?: string;
  items: { label: string; value: string; bg?: string }[];
}
export interface TopShare {
  label: string;
  value: string;
  bg?: string;
}
export interface TopSummary {
  goals: TopGoal[];
  stats: TopStatGroup[];
  shares: TopShare[][];
  rest: TopCell[];
}

/** 값처럼 보이는 글자 — '230' · '1,200' · '41.0%' · '-' · '₩1,000' */
const isValue = (t: string) =>
  /^[₩$]?\s*-?[\d,]+(\.\d+)?\s*(%|건|대|명)?$/.test(t.trim()) || t.trim() === '-';
const SHARE = /^([^:：]{1,30}?)\s*[:：]\s*(.+)$/;
/** 시트 칸 색 — 머리글 칸의 회색('#d9d9d9')은 구분용이라 뺀다(칩의 점은 담당자 · 통화처럼 뜻 있는 색만) */
const tint = (bg: string | undefined) => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(bg ?? '');
  if (!m) return undefined;
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h!, 16));
  return Math.max(r!, g!, b!) - Math.min(r!, g!, b!) < 16 ? undefined : bg;
};

export function parseSheetTop(top: SheetMirrorTopRowDto[] | undefined): TopSummary {
  const out: TopSummary = { goals: [], stats: [], shares: [], rest: [] };
  if (!top?.length) return out;
  const grid = new Map<string, TopCell>();
  const at = (r: number, c: number) => grid.get(`${r}:${c}`);
  for (const row of top)
    for (const c of row.cells) grid.set(`${row.row}:${c.col}`, { text: c.text, bg: tint(c.bg) });
  const used = new Set<string>();
  const take = (r: number, c: number) => used.add(`${r}:${c}`);
  const free = (r: number, c: number) => !!at(r, c) && !used.has(`${r}:${c}`);
  const isLabel = (r: number, c: number) =>
    free(r, c) && !isValue(at(r, c)!.text) && !SHARE.test(at(r, c)!.text);
  const rowsAsc = top.map((r) => r.row).sort((a, b) => a - b);
  const colsOf = (r: number) =>
    (top.find((x) => x.row === r)?.cells ?? []).map((c) => c.col).sort((a, b) => a - b);

  // 1) 비율 칩 — 빈 열이 둘 넘게 벌어지거나 바탕색이 있다 · 없다로 바뀌면 다른 묶음
  for (const r of rowsAsc) {
    let group: TopShare[] = [];
    let prev: { col: number; bg: boolean } | null = null;
    for (const c of colsOf(r)) {
      const cell = at(r, c)!;
      const m = SHARE.exec(cell.text);
      if (!m) continue;
      take(r, c);
      const hasBg = !!cell.bg;
      if (prev && (c - prev.col > 2 || prev.bg !== hasBg) && group.length) {
        out.shares.push(group);
        group = [];
      }
      group.push({ label: m[1]!.trim(), value: m[2]!.trim(), bg: cell.bg });
      prev = { col: c, bg: hasBg };
    }
    if (group.length) out.shares.push(group);
  }

  // 2) 표 — 모서리 칸: 오른쪽 칸과 아래 칸이 모두 이름
  for (const r of rowsAsc) {
    for (const c of colsOf(r)) {
      if (!isLabel(r, c) || !isLabel(r, c + 1) || !isLabel(r + 1, c)) continue;
      const heads: number[] = [];
      for (let x = c + 1; isLabel(r, x); x++) heads.push(x);
      const lines: number[] = [];
      for (let y = r + 1; isLabel(y, c); y++) lines.push(y);
      take(r, c);
      for (const x of heads) {
        take(r, x);
        const goal: TopGoal = { label: at(r, x)!.text, extra: [] };
        for (const y of lines) {
          const v = free(y, x) ? at(y, x)!.text : '';
          if (v) take(y, x);
          if (!v) continue;
          const name = at(y, c)!.text;
          if (/[률율]/.test(goal.label) && !goal.rate && !goal.done) goal.rate = v;
          else if (/목표/.test(name) && goal.target === undefined) goal.target = v;
          else if (/[률율]/.test(name) && v.endsWith('%') && goal.rate === undefined) goal.rate = v;
          else if (/달성|건수|실적/.test(name) && goal.done === undefined) goal.done = v;
          else goal.extra.push([name, v]);
        }
        out.goals.push(goal);
      }
      for (const y of lines) take(y, c);
    }
  }

  // 3) 숫자 묶음 — 이름 칸 바로 아래가 값
  for (const r of rowsAsc) {
    let group: TopStatGroup | null = null;
    let lastCol = -9;
    /** 값이 없는 이름 칸 — 바로 오른쪽에 묶음이 붙으면 그 제목, 아니면 나머지로 남는다 */
    let title: number | null = null;
    for (const c of colsOf(r)) {
      if (!isLabel(r, c)) continue;
      const below = at(r + 1, c);
      if (!below || !free(r + 1, c) || !isValue(below.text)) {
        title = c;
        lastCol = c;
        continue;
      }
      if (!group || c - lastCol > 1) {
        if (group) out.stats.push(group);
        const titled = title !== null && title === c - 1;
        group = { title: titled ? at(r, title!)!.text : undefined, items: [] };
        if (titled) take(r, title!);
        title = null;
      }
      group.items.push({ label: at(r, c)!.text, value: below.text, bg: at(r, c)!.bg });
      take(r, c);
      take(r + 1, c);
      lastCol = c;
    }
    if (group) out.stats.push(group);
  }

  for (const r of rowsAsc) for (const c of colsOf(r)) if (free(r, c)) out.rest.push(at(r, c)!);
  return out;
}

/**
 * 비율 칩의 시트 칸 색 → 칩 색표(이름 → [바탕, 글자]). 2026-10-23 오너 — 담당자 비율 칸은 "언제든지 삭제되고
 * 추가될 수 있으니 시트 내용이 바뀌는거에 따라 콘솔도 업데이트" → 표의 담당자 칩 색도 여기서 가져온다.
 */
export function shareColors(summary: TopSummary): Record<string, readonly [string, string]> {
  const out: Record<string, readonly [string, string]> = {};
  for (const g of summary.shares)
    for (const s of g) if (s.bg && !out[s.label]) out[s.label] = [s.bg, inkOn(s.bg)];
  return out;
}

/** 시트 바탕색 위에 읽히는 글자색(밝은 칸은 검정, 어두운 칸은 흰색) */
export function inkOn(bg: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(bg);
  if (!m) return 'inherit';
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#1f1f1f' : '#ffffff';
}

/** '100%' → 100 · 글자가 아니면 null */
export function percentOf(rate: string | undefined): number | null {
  const m = /(-?[\d.]+)\s*%/.exec(rate ?? '');
  return m ? Number(m[1]) : null;
}
